import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { View, StyleSheet, ScrollView, } from 'react-native';
import { truncate } from 'lodash';
import Button from 'design-system/Button';
import { rem } from 'design-system/values';
import { AppView, CardView, SettingsItemInput, Spacer } from 'design-system';
import { IconBlog } from 'design-system/icons';
import useColors from 'hooks/useColors';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { useAppSelector } from 'store/hooks';
import { formatAmount, formatSortCodeAndAccountNumber } from 'utils/formatting';
import { selectIsScrambled, useFetchOld, useKeyboardAdjustPan } from 'hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import MoneyInput from 'design-system/MoneyInput';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import LogoBankAccount, { CIRCLE_BORDER_SIZE, } from 'design-system/LogoBankAccount';
import { Avatar } from 'design-system/avatar';
import Modal from 'utils/packages/Modal';
import useIsTablet from 'hooks/useIsTablet';
import { getHandleInfo } from '../actions';
const minAmount = 0;
const EnterAmountView = ({ onSubmit, loading, to, initialReference, initialAmount, buttonLabel, available, }) => {
    useKeyboardAdjustPan();
    const [reference, setReference] = useState(initialReference || '');
    const referenceTextInputRef = useRef(null);
    const colors = useColors();
    const navigation = useMainStackNavigation();
    const [fetchedContact, , isFetching, fetch] = useFetchOld({ throws: true });
    const name = useMemo(() => {
        if (to.itemType === 'account') {
            return to.account.customName || to.account.name;
        }
        if (to.itemType === 'payee') {
            return to.payee.name;
        }
        if (to.contact &&
            ('nameToShow' in to.contact || 'givenName' in to.contact)) {
            if (to.contact.nameToShow) {
                return to.contact.nameToShow;
            }
            return `${to.contact.givenName || ''}${to.contact.familyName ? ` ${to.contact.familyName}` : ''}`;
        }
        if (fetchedContact) {
            return fetchedContact.firstName;
        }
        return null;
    }, [fetchedContact, to]);
    useEffect(() => {
        if (to.itemType === 'pay' && to.contact?.handle && !name) {
            fetch(getHandleInfo(to.contact.handle)).catch(() => {
                navigation.pop();
                Modal.showError(`User with @EmmaTag ${to.contact?.handle} not found`, undefined, 'Error', 'Okay');
            });
        }
    }, [name, to]);
    const icon = useMemo(() => {
        if (to.itemType === 'account') {
            return <LogoBankAccount size="R40" data={to.account}/>;
        }
        if (to.itemType === 'payee') {
            return <LogoBankAccount size="R40" data={{}}/>;
        }
        let imageUrl = to.contact && 'thumbnailPath' in to.contact
            ? to.contact?.thumbnailPath
            : undefined;
        if (!imageUrl && fetchedContact) {
            imageUrl = fetchedContact.img;
        }
        return (<Avatar name={name || '?'} lastName={to.contact && 'familyName' in to.contact
                ? to.contact?.familyName || ''
                : undefined} color={colors.background.brandLight} fontColor={colors.text.textOnBrand} imageUrl={imageUrl} size={rem(40)} borderWidth={CIRCLE_BORDER_SIZE} small/>);
    }, [
        colors.background.brandLight,
        colors.text.textOnBrand,
        name,
        to,
        fetchedContact,
    ]);
    const extraDetails = useMemo(() => {
        if (to.itemType === 'account') {
            return formatSortCodeAndAccountNumber(to.account.sortCode, to.account.accountNumber);
        }
        if (to.itemType === 'payee') {
            return formatSortCodeAndAccountNumber(to.payee.sortCode, to.payee.accountNumber);
        }
        if (to.contact && 'handle' in to.contact && to.contact.handle) {
            return `@${to.contact.handle}`;
        }
        return to.phoneNumber;
    }, [to]);
    const isTablet = useIsTablet();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            headerTitle: () => isFetching ? ('') : (<View>
            {name ? (<Text Text-18 centered numberOfLines={1}>
                {isTablet ? name : truncate(name, { length: 24 })}
              </Text>) : null}
            {extraDetails ? (<Text TextThin-14 Secondary centered>
                {extraDetails}
              </Text>) : null}
          </View>),
            headerRight: () => (isFetching ? null : icon),
        });
    }, [colors.text.primary, extraDetails, icon, isFetching, isTablet, name]);
    const [exceededMax, setExceededMax] = useState(false);
    const [disabled, setDisabled] = useState(true);
    const amountRef = useRef(0);
    const onAmountChange = useCallback((value) => {
        amountRef.current = value;
        setExceededMax(false);
        if (value >= minAmount && value > 0) {
            setDisabled(false);
        }
        else {
            setDisabled(true);
        }
    }, []);
    const isScrambled = useAppSelector(selectIsScrambled);
    const submit = useCallback(async () => {
        if (!amountRef.current || isScrambled)
            return;
        if (to.itemType === 'payee') {
            Modal.showConfirmation({
                title: 'Payment confirmation',
                subtitle: 'Please confirm this is a legitimate payee and all the details are correct; if you are on the phone with someone, please hang up and cancel the payment.',
                confirmButtonTitle: 'Cancel',
                cancelButtonTitle: 'Confirm and Pay',
                onPressCancel: () => onSubmit(amountRef.current, reference),
            });
        }
        else {
            onSubmit(amountRef.current, reference);
        }
    }, [isScrambled, onSubmit, reference, to.itemType]);
    const numPadHeader = useMemo(() => available && (<View style={styles.numpadHeader}>
          <Text NumbersThin-14 Secondary>
            {exceededMax ? (<Text TextThin-14 Red>
                Only{' '}
                <Text NumbersThin-14 Red>
                  {formatAmount(available.amount, available.currency)}
                </Text>{' '}
                available
              </Text>) : (<>
                <Text TextThin-14 Secondary>
                  Available:{' '}
                </Text>
                <Text NumbersThin-14 Secondary>
                  {formatAmount(available.amount, available.currency)}
                </Text>
              </>)}
          </Text>
        </View>), [available, exceededMax]);
    const onExceedMax = useCallback(() => {
        setExceededMax(true);
    }, []);
    const moneyInputColors = useMemo(() => ({
        input: colors.text.primary,
        pinpad: colors.text.primary,
        hintColor: colors.text.secondary,
    }), [colors.text.primary, colors.text.secondary]);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, styles.container);
    return (<AppView avoidHeader pointerEvents={loading ? 'none' : 'auto'}>
      <StatusBar barStyle="dark-content" animated/>
      <ScrollView scrollEnabled={false} keyboardShouldPersistTaps="never" contentContainerStyle={paddingBottom}>
        <MoneyInput flex initialValue={initialAmount} colors={moneyInputColors} min={minAmount} max={available?.amount} onExceedMax={onExceedMax} pinPadStyle={styles.pinPad} numPadHeader={numPadHeader} onValueChange={onAmountChange} disableEventBus>
          <View style={styles.referenceContainer}>
            <CardView>
              <SettingsItemInput ref={referenceTextInputRef} value={reference} onChangeText={setReference} placeholder="Add a note" returnKeyType="done" returnKeyLabel="Done" icon={<IconBlog />} description="Reference"/>
            </CardView>
          </View>
        </MoneyInput>
        <Spacer h={30}/>
        <View style={styles.buttonContainer}>
          <Button brand title={buttonLabel} disabled={disabled} isFetching={loading} onPress={submit}/>
        </View>
      </ScrollView>
    </AppView>);
};
const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    buttonContainer: {
        paddingHorizontal: rem(16),
    },
    referenceContainer: {
        width: '100%',
        marginHorizontal: rem(16),
    },
    pinPad: {
        marginTop: rem(10),
    },
    numpadHeader: {
        flex: 2,
    },
});
export default EnterAmountView;
